import { fourEClient } from "../api/fourEClient";
import { getProfileContinuityStatus, setProfileContinuityStatus } from "./profileContinuity";

const cleanField = (field) => String(field || "").replace(/[^a-z0-9_]+/gi, "");

const normalizeText = (value) => String(value || "").replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();

const normalizeState = (state = {}, pack = null) => {
  const buildOptin = state.buildOptin === true || state.build_optin === 1 || state.build_optin === "1";
  const aiUseOptin = buildOptin && (state.aiUseOptin === true || state.ai_use_optin === 1 || state.ai_use_optin === "1");
  return {
    explicitConsent: buildOptin || aiUseOptin,
    buildOptin,
    aiUseOptin,
    hasPack: buildOptin && Boolean(state.hasPack || state.hasData || state.has_data || pack),
    updatedAt: String(state.updatedAt || state.updated_at || ""),
    lastRebuiltAt: String(state.lastRebuiltAt || state.last_rebuilt_at || state.updatedAt || state.updated_at || ""),
    profile: pack && typeof pack === "object" ? pack : null,
    aadUser: state.aadUser || state.aad_user || "",
  };
};

const requireVaultUnlocked = async ({ interactive = true, waitMs = 20000 } = {}) => {
  const session = typeof window !== "undefined" ? window.MDVaultSession : null;
  if (!session?.ensureUnlocked) {
    const error = new Error("Sesja sejfu jest niedostępna.");
    error.code = "vault_session_missing";
    throw error;
  }
  const unlocked = await session.ensureUnlocked({ interactive, waitMs }).catch((error) => { throw error; });
  const ok = Boolean(unlocked?.ok || unlocked?.unlocked || unlocked === true || unlocked?.state?.unlocked || session.isUnlocked?.());
  if (!ok) {
    const error = new Error("Odblokuj sejf, aby pokazać dane profilu.");
    error.code = "vault_locked";
    throw error;
  }
  return true;
};

export const syncProfileContinuityFromRest = async () => {
  const response = await fourEClient.profileGet();
  if (!response?.ok) throw new Error(response?.message || "Nie udało się pobrać profilu ciągłości.");
  return setProfileContinuityStatus(normalizeState(response.state, response.pack || null));
};

export const saveProfileContinuityConsentRest = async ({ buildOptin, aiUseOptin }) => {
  const response = await fourEClient.profileConsent({ buildOptin: Boolean(buildOptin), aiUseOptin: Boolean(aiUseOptin) });
  if (!response?.ok) throw new Error(response?.message || "Nie udało się zapisać zgody profilu ciągłości.");
  return setProfileContinuityStatus(normalizeState(response.state, getProfileContinuityStatus().profile));
};

export const deleteProfileContinuityRest = async () => {
  const response = await fourEClient.profileDelete();
  if (!response?.ok) throw new Error(response?.message || "Nie udało się usunąć profilu ciągłości.");
  return setProfileContinuityStatus(normalizeState(response.state, null));
};

export const getProfileAad = (uid, field) => `mrf:profile:v1|user:${String(uid || "").trim()}|field:${cleanField(field)}`;

const parseJsonSafe = (value, fallback) => {
  try {
    return JSON.parse(String(value || ""));
  } catch (_) {
    return fallback;
  }
};

const decryptProfileText = async (field) => {
  if (!field?.iv || !field?.ct || !window?.morfeuszE2E?.decryptText) return null;
  try {
    return await window.morfeuszE2E.decryptText(field.iv, field.ct, String(field.aad || ""));
  } catch (_) {
    return await window.morfeuszE2E.decryptText(field.iv, field.ct, "[object Object]");
  }
};

const decryptProfileJson = async (field, fallback) => {
  const text = await decryptProfileText(field);
  if (text === null || typeof text === "undefined") return fallback;
  return parseJsonSafe(text, fallback);
};

const countArrayToMap = (arr) => {
  const map = {};
  if (Array.isArray(arr)) {
    arr.forEach((item) => {
      const key = normalizeText(item?.key || item);
      const score = Number(typeof item?.score !== "undefined" ? item.score : 1);
      if (key && Number.isFinite(score) && score > 0) map[key] = score;
    });
  } else if (arr && typeof arr === "object") {
    Object.entries(arr).forEach(([key, score]) => {
      const n = Number(score || 0);
      if (key && Number.isFinite(n) && n > 0) map[key] = n;
    });
  }
  return map;
};

const mapToTopEntries = (map, limit = 20) => Object.entries(map || {})
  .map(([key, score]) => ({ key, score: Number(score || 0) }))
  .filter((item) => item.key && item.score > 0)
  .sort((a, b) => b.score - a.score)
  .slice(0, limit);

const readableMotifs = (value) => {
  if (Array.isArray(value)) return countArrayToMap(value);
  if (value?.motif_counts) return countArrayToMap(value.motif_counts);
  return countArrayToMap(value || {});
};

const readableEmotions = (value) => {
  if (Array.isArray(value)) return countArrayToMap(value);
  if (value?.emotion_counts) return countArrayToMap(value.emotion_counts);
  return countArrayToMap(value || {});
};

export const decryptReadableProfilePack = async (pack) => {
  if (!pack || typeof pack !== "object") return null;
  await requireVaultUnlocked({ interactive: true, waitMs: 20000 });

  const [tldr, motifs, emotions, timeline, samples] = await Promise.all([
    decryptProfileText(pack.tldr).catch(() => ""),
    decryptProfileJson(pack.motifs, []).catch(() => []),
    decryptProfileJson(pack.emotions, []).catch(() => []),
    decryptProfileJson(pack.timeline, []).catch(() => []),
    decryptProfileJson(pack.samples, []).catch(() => []),
  ]);

  return {
    tldr: typeof tldr === "string" ? normalizeText(tldr) : "",
    motifs: readableMotifs(motifs),
    emotions: readableEmotions(emotions),
    motifEntries: mapToTopEntries(readableMotifs(motifs), 20),
    emotionEntries: mapToTopEntries(readableEmotions(emotions), 12),
    timeline: Array.isArray(timeline) ? timeline : [],
    samples: Array.isArray(samples) ? samples : [],
  };
};

export const getReadableProfileContinuityRest = async () => {
  const response = await fourEClient.profileGet();
  if (!response?.ok) throw new Error(response?.message || "Nie udało się pobrać profilu ciągłości.");
  const status = setProfileContinuityStatus(normalizeState(response.state, response.pack || null));
  if (!response.pack) return { ok: true, state: status, profile: null, hasPack: false };
  const profile = await decryptReadableProfilePack(response.pack);
  return { ok: true, state: status, profile, hasPack: Boolean(profile) };
};

const buildHistoryContext = ({ tldr, motifs, emotions, samples }) => {
  const motifEntries = mapToTopEntries(readableMotifs(motifs), 6).map((item) => `${item.key}(${Math.round(item.score)})`);
  const emotionEntries = mapToTopEntries(readableEmotions(emotions), 4).map((item) => item.key);
  const lines = [];
  lines.push("[PROFIL_AUTORA_V2]");
  if (tldr) lines.push(tldr);
  if (motifEntries.length) lines.push(`Powracające motywy: ${motifEntries.join(", ")}`);
  if (emotionEntries.length) lines.push(`Dominujące emocje: ${emotionEntries.join(", ")}`);
  const topSamples = Array.isArray(samples) ? samples.slice(0, 5) : [];
  if (topSamples.length) {
    lines.push("[HISTORIA_TOPK]");
    topSamples.forEach((sample) => {
      const tags = Array.isArray(sample.motifs) && sample.motifs.length ? ` # ${sample.motifs.join(", ")}` : "";
      lines.push(`- (${String(sample.date || "")}) ${normalizeText(sample.summary || "").slice(0, 220)}${tags}`);
    });
    lines.push("[/HISTORIA_TOPK]");
  }
  lines.push("[INSTRUKCJA_KONTEKSTU]Masz kontekst historii użytkownika. Priorytet ma bieżący sen; historię traktuj jako hipotezy. Jeśli widać rozbieżność między bieżącym snem a profilem, wskaż ją. Na końcu dodaj sekcję „Na tle historii” (3–5 zdań) i „Stałe tematy (max 3)”.[/INSTRUKCJA_KONTEKSTU]");
  lines.push("[/PROFIL_AUTORA_V2]");
  return lines.join("\n");
};

export const buildProfileContextForAnalysisRest = async () => {
  const cached = getProfileContinuityStatus();
  if (!cached.buildOptin) return { enabled: false, reason: "build_optin_disabled", buildOptin: false, aiUseOptin: false, hasPack: false };
  if (!cached.aiUseOptin) return { enabled: false, reason: "ai_use_optin_disabled", buildOptin: true, aiUseOptin: false, hasPack: cached.hasPack };

  const response = await fourEClient.profileGet();
  if (!response?.ok) return { enabled: false, reason: response?.code || "profile_get_failed", buildOptin: true, aiUseOptin: true, hasPack: cached.hasPack };
  const status = setProfileContinuityStatus(normalizeState(response.state, response.pack || null));
  if (!status.aiUseOptin) return { enabled: false, reason: "ai_use_optin_disabled", buildOptin: status.buildOptin, aiUseOptin: false, hasPack: status.hasPack };
  if (!response.pack) return { enabled: false, reason: "profile_pack_missing", buildOptin: true, aiUseOptin: true, hasPack: false };

  try {
    await requireVaultUnlocked({ interactive: false, waitMs: 2500 });
  } catch (_) {
    return { enabled: false, reason: "vault_locked", buildOptin: true, aiUseOptin: true, hasPack: true };
  }

  const pack = response.pack;
  const [tldr, motifs, emotions, timeline, samples] = await Promise.all([
    decryptProfileText(pack.tldr).catch(() => ""),
    decryptProfileJson(pack.motifs, []).catch(() => []),
    decryptProfileJson(pack.emotions, []).catch(() => []),
    decryptProfileJson(pack.timeline, []).catch(() => []),
    decryptProfileJson(pack.samples, []).catch(() => []),
  ]);

  const profile = {
    tldr: typeof tldr === "string" ? normalizeText(tldr) : "",
    motifs: readableMotifs(motifs),
    emotions: readableEmotions(emotions),
    timeline: Array.isArray(timeline) ? timeline.slice(0, 12) : [],
    samples: Array.isArray(samples) ? samples.slice(0, 8) : [],
  };

  return {
    enabled: true,
    reason: "profile_ready",
    buildOptin: true,
    aiUseOptin: true,
    hasPack: true,
    ...profile,
    contextText: buildHistoryContext(profile),
    updatedAt: status.updatedAt,
  };
};

export const saveProfilePackRest = async (pack) => {
  const response = await fourEClient.profileSave(pack);
  if (!response?.ok) throw new Error(response?.message || "Nie udało się zapisać profilu ciągłości.");
  setProfileContinuityStatus(normalizeState(response.state, response.pack || pack || null));
  return response;
};
